import { EventEmitter } from 'eventemitter3';
import { ICSElement } from '../types/interfaces';
import { 
  IDevToolsManager, 
  IElementInspector, 
  IPerformanceProfiler,
  DevToolsEvent, 
  DevToolsEventType,
  StructureSnapshot
} from '../types/devtools-interfaces';
import { ElementInspector } from './ElementInspector';
import { PerformanceProfiler } from './PerformanceProfiler';

/**
 * Центральный менеджер DevTools для CSElement
 * Координирует работу инспектора элементов и профилировщика производительности
 */
export class DevToolsManager implements IDevToolsManager {
  private _eventEmitter: EventEmitter;
  private _inspector: IElementInspector;
  private _profiler: IPerformanceProfiler;
  private _registeredElements: Set<ICSElement>;
  private _isEnabled: boolean;
  private _autoTrackElements: boolean;

  constructor() {
    this._eventEmitter = new EventEmitter();
    this._inspector = new ElementInspector();
    this._profiler = new PerformanceProfiler();
    this._registeredElements = new Set();
    this._isEnabled = true;
    this._autoTrackElements = true;

    this.setupEventHandlers();
  }

  get inspector(): IElementInspector {
    return this._inspector;
  }

  get profiler(): IPerformanceProfiler {
    return this._profiler;
  }

  /**
   * Подписаться на события DevTools
   */
  addEventListener(eventType: DevToolsEventType, handler: (event: DevToolsEvent) => void): void {
    this._eventEmitter.on(eventType as string, handler);
  }

  /**
   * Отписаться от событий DevTools
   */
  removeEventListener(eventType: DevToolsEventType, handler: (event: DevToolsEvent) => void): void {
    this._eventEmitter.off(eventType as string, handler);
  }

  /**
   * Отправить событие DevTools
   */
  emitEvent(event: DevToolsEvent): void {
    if (!this._isEnabled) return;

    this._eventEmitter.emit(event.type, event);

    // Логируем важные события
    if (['error:occurred', 'performance:measured'].includes(event.type)) {
      console.log(`🔧 DevTools Event: ${event.type}`, event);
    }
  }

  // Сохраняем интерфейс IDevToolsManager
  on(eventType: DevToolsEventType, handler: (event: DevToolsEvent) => void): void {
    this.addEventListener(eventType, handler);
  }

  off(eventType: DevToolsEventType, handler: (event: DevToolsEvent) => void): void {
    this.removeEventListener(eventType, handler);
  }

  emitDevToolsEvent(event: DevToolsEvent): void {
    this.emitEvent(event);
  }

  /**
   * Зарегистрировать элемент для отслеживания
   */
  registerElement(element: ICSElement): void {
    if (!this._isEnabled) return;

    this._registeredElements.add(element);

    // Отправляем событие о регистрации
    this.emitEvent({
      type: 'element:created',
      timestamp: Date.now(),
      elementId: element.id,
      data: {
        name: element.name,
        type: element.constructor.name
      }
    });

    // Настраиваем отслеживание событий элемента
    this.setupElementTracking(element);
  }

  /**
   * Отменить регистрацию элемента
   */
  unregisterElement(element: ICSElement): void {
    const removed = this._registeredElements.delete(element);
    
    if (removed) {
      this.emitDevToolsEvent({
        type: 'element:destroyed',
        timestamp: Date.now(),
        elementId: element.id,
        data: {
          name: element.name,
          type: element.constructor.name
        }
      });
    }
  }

  /**
   * Получить все зарегистрированные элементы
   */
  getRegisteredElements(): ICSElement[] {
    return Array.from(this._registeredElements);
  }

  /**
   * Создать глобальный снимок всех зарегистрированных элементов
   */
  createGlobalSnapshot(): Map<string, StructureSnapshot> {
    const snapshots = new Map<string, StructureSnapshot>();

    // Находим корневые элементы (элементы без родителей)
    const rootElements = this.findRootElements();

    rootElements.forEach(rootElement => {
      try {
        const snapshot = this._inspector.createSnapshot(rootElement);
        snapshots.set(rootElement.id, snapshot);
      } catch (error) {
        this.emitDevToolsEvent({
          type: 'error:occurred',
          timestamp: Date.now(),
          elementId: rootElement.id,
          data: {
            error: error instanceof Error ? error.message : 'Unknown error',
            operation: 'createSnapshot'
          }
        });
      }
    });

    return snapshots;
  }

  /**
   * Включить/выключить DevTools
   */
  setEnabled(enabled: boolean): void {
    this._isEnabled = enabled;
    this._profiler.setEnabled(enabled);

    this.emitDevToolsEvent({
      type: enabled ? 'element:created' : 'element:destroyed',
      timestamp: Date.now(),
      data: {
        devToolsEnabled: enabled
      }
    });
  }

  /**
   * Проверить, включены ли DevTools
   */
  isEnabled(): boolean {
    return this._isEnabled;
  }

  /**
   * Включить/выключить автоматическое отслеживание элементов
   */
  setAutoTrackElements(enabled: boolean): void {
    this._autoTrackElements = enabled;
  }

  /**
   * Проверить, включено ли автоматическое отслеживание
   */
  isAutoTrackElementsEnabled(): boolean {
    return this._autoTrackElements;
  }

  /**
   * Получить статистику DevTools
   */
  getStats(): {
    registeredElements: number;
    isEnabled: boolean;
    profilerStats: any;
    memoryUsage: number;
  } {
    return {
      registeredElements: this._registeredElements.size,
      isEnabled: this._isEnabled,
             profilerStats: (this._profiler as any).createSnapshot?.() || {},
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Выполнить диагностику системы
   */
  runDiagnostics(): {
    issues: Array<{
      type: 'warning' | 'error' | 'info';
      message: string;
      elementId?: string;
      suggestion?: string;
    }>;
    summary: {
      totalIssues: number;
      criticalIssues: number;
      performance: 'good' | 'warning' | 'critical';
    };
  } {
    const issues: any[] = [];
    
    // Проверяем производительность
    const profilerReport = this._profiler.getReport();
    if (profilerReport.summary.averageDuration > 100) {
      issues.push({
        type: 'warning',
        message: `Средняя длительность операций: ${profilerReport.summary.averageDuration.toFixed(2)}мс`,
        suggestion: 'Рассмотрите оптимизацию медленных операций'
      });
    }

    // Проверяем использование памяти
    const memoryUsage = this.estimateMemoryUsage();
    if (memoryUsage > 50 * 1024 * 1024) { // 50MB
      issues.push({
        type: 'warning',
        message: `Высокое использование памяти: ${(memoryUsage / 1024 / 1024).toFixed(2)}MB`,
        suggestion: 'Проверьте наличие утечек памяти'
      });
    }

    // Проверяем количество элементов
    if (this._registeredElements.size > 10000) {
      issues.push({
        type: 'warning',
        message: `Большое количество отслеживаемых элементов: ${this._registeredElements.size}`,
        suggestion: 'Рассмотрите использование пагинации или ленивой загрузки'
      });
    }

    // Проверяем глубокие структуры
    this._registeredElements.forEach(element => {
      try {
        const info = this._inspector.inspect(element);
        if (info.depth > 20) {
          issues.push({
            type: 'warning',
            message: `Очень глубокая структура (глубина: ${info.depth})`,
            elementId: element.id,
            suggestion: 'Рассмотрите рефакторинг структуры'
          });
        }
      } catch (error) {
        issues.push({
          type: 'error',
          message: `Ошибка при инспектировании элемента: ${error}`,
          elementId: element.id
        });
      }
    });

    const criticalIssues = issues.filter(issue => issue.type === 'error').length;
    let performance: 'good' | 'warning' | 'critical' = 'good';
    
    if (criticalIssues > 0) {
      performance = 'critical';
    } else if (issues.length > 5) {
      performance = 'warning';
    }

    return {
      issues,
      summary: {
        totalIssues: issues.length,
        criticalIssues,
        performance
      }
    };
  }

  /**
   * Экспортировать данные DevTools
   */
  exportData(options: {
    includeSnapshots?: boolean;
    includeProfilerData?: boolean;
    includeElementData?: boolean;
    timeRange?: { start: number; end: number };
  } = {}): {
    metadata: {
      exportedAt: number;
      version: string;
      elementCount: number;
    };
    snapshots?: Map<string, StructureSnapshot>;
    profilerData?: any;
    elements?: any[];
  } {
    const {
      includeSnapshots = true,
      includeProfilerData = true,
      includeElementData = false,
      timeRange
    } = options;

    const exportData: any = {
      metadata: {
        exportedAt: Date.now(),
        version: '1.0.0',
        elementCount: this._registeredElements.size
      }
    };

    if (includeSnapshots) {
      exportData.snapshots = this.createGlobalSnapshot();
    }

    if (includeProfilerData) {
      exportData.profilerData = this._profiler.getReport(timeRange);
    }

    if (includeElementData) {
      exportData.elements = Array.from(this._registeredElements).map(element => {
        return this._inspector.inspect(element);
      });
    }

    return exportData;
  }

  /**
   * Импортировать данные DevTools
   */
  importData(data: any): boolean {
    try {
      if (data.metadata?.version !== '1.0.0') {
        console.warn('Неподдерживаемая версия данных DevTools');
        return false;
      }

      // Можно добавить логику импорта снимков и данных профилировщика
      console.log('Импорт данных DevTools успешно завершен');
      return true;
    } catch (error) {
      this.emitDevToolsEvent({
        type: 'error:occurred',
        timestamp: Date.now(),
        data: {
          error: error instanceof Error ? error.message : 'Import error',
          operation: 'importData'
        }
      });
      return false;
    }
  }

  /**
   * Настройка обработчиков событий
   */
  private setupEventHandlers(): void {
    // Слушаем события профилировщика (если поддерживается)
    const profilerEmitter = this._profiler as any;
    if (profilerEmitter.on) {
      profilerEmitter.on('measurement:complete', (metrics: any) => {
        this.emitDevToolsEvent({
          type: 'performance:measured',
          timestamp: Date.now(),
          data: metrics
        });
      });
    }
  }

  /**
   * Настройка отслеживания событий элемента
   */
  private setupElementTracking(element: ICSElement): void {
    if (!this._autoTrackElements) return;

    // Если элемент поддерживает события, подписываемся на них
    const elementEmitter = element as any;
    if (typeof elementEmitter.on === 'function') {
      elementEmitter.on('data:changed', (event: any) => {
        this.emitDevToolsEvent({
          type: 'element:modified',
          timestamp: Date.now(),
          elementId: element.id,
          data: event
        });
      });

      elementEmitter.on('element:added', (event: any) => {
        this.emitDevToolsEvent({
          type: 'structure:changed',
          timestamp: Date.now(),
          elementId: element.id,
          data: { type: 'child_added', ...event }
        });
      });

      elementEmitter.on('element:removed', (event: any) => {
        this.emitDevToolsEvent({
          type: 'structure:changed',
          timestamp: Date.now(),
          elementId: element.id,
          data: { type: 'child_removed', ...event }
        });
      });
    }
  }

  /**
   * Найти корневые элементы (без родителей)
   */
  private findRootElements(): ICSElement[] {
    const rootElements: ICSElement[] = [];
    
    this._registeredElements.forEach(element => {
      // Простая проверка: если элемент не является дочерним для других зарегистрированных элементов
      let isRoot = true;
      
      this._registeredElements.forEach(potentialParent => {
        if (potentialParent !== element) {
          const csParent = potentialParent as any;
          if (csParent._elements?.has && Array.from(csParent._elements.values()).includes(element)) {
            isRoot = false;
          }
        }
      });
      
      if (isRoot) {
        rootElements.push(element);
      }
    });

    return rootElements;
  }

  /**
   * Оценить использование памяти
   */
  private estimateMemoryUsage(): number {
    try {
      if (typeof performance !== 'undefined' && 'memory' in performance) {
        const memory = (performance as any).memory;
        return memory.usedJSHeapSize || 0;
      }
      return 0;
    } catch {
      return 0;
    }
  }
} 
